/**
 * Coach About Modal — explains what the AI Coach does + tier breakdown.
 *
 * Opened from the Free/Pro badge in the Live Coach Feed header. Doubles as:
 *   - First-time onboarding (auto-opens if `coachOnboardingSeen` localStorage
 *     flag isn't set) — so new users learn what they're looking at.
 *   - Ongoing reference — click the badge anytime to revisit what's free vs
 *     what Pro unlocks.
 *   - Upgrade surface — the Pro tier section doubles as the upsell pitch. The
 *     "Upgrade to Pro" button routes to Settings → License, where tester codes
 *     are redeemed.
 *
 * Props:
 *   userTier    'free' | 'pro'
 *   onClose     () => void
 *   onUpgrade   () => void         // stub for now; wire to real flow later
 *   darkMode    bool
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Sparkles, Check, Lock, Newspaper, TrendingUp, Users, Zap,
  Activity, Timer, Target, ShieldCheck, BarChart3
} from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// Tier feature lists
// ────────────────────────────────────────────────────────────────────────────
// Free-tier features — everything shipped today as `tier: 'free'` analyzers
// plus the structural features (auto-tagging, historical cache, etc.).

const FREE_FEATURES = [
  { icon: Target,     label: 'Day grade (A/B/C/D/F)',          detail: 'Ranked signal across trade selection, risk, and discipline.' },
  { icon: Activity,   label: 'Mistake clusters',               detail: 'Which bad habit cost the most today.' },
  { icon: Zap,        label: 'Setup & ATM spotlights',         detail: 'Which playbook carried the day; which bled.' },
  { icon: Timer,      label: 'Time-of-day windows',            detail: 'When you traded best vs worst.' },
  { icon: ShieldCheck,label: 'First-trade + overtrading flags',detail: 'Session lifecycle markers — tilt, fatigue, revenge.' },
  { icon: Newspaper,  label: 'News/event auto-tagging',        detail: 'Trades within 30 min of an event auto-tag that event.' },
  { icon: BarChart3,  label: '30-day discipline trends',       detail: 'Cross-session signals — resolved issues, new leaks, drift.' }
];

// Pro-tier features — what Pro unlocks on top. Each entry is a promise for
// analyzers that are pro-gated (or will be, once promoted).
const PRO_FEATURES = [
  {
    icon: Timer,
    label: 'Mid-trade event countdown',
    detail: 'High/medium news event landing in the next 30 minutes? Pro whispers the countdown alongside your real history with that event — e.g. "FOMC in 18m · −$240 avg · 6 prior" — while you\'re still in the trade.'
  },
  {
    icon: Newspaper,
    label: 'Historical event citations',
    detail: '"CPI days: you\'re −$240 avg over 6 months, 28% WR vs 52% baseline." See your real track record around every news type.'
  },
  {
    icon: TrendingUp,
    label: 'Baseline comparisons',
    detail: 'Today\'s PF, win rate, and drawdown compared against your own 30/90/180-day rolling averages. Know what "normal" looks like for YOU.'
  },
  {
    icon: Users,
    label: 'Cross-account comparison',
    detail: 'Same setup, multiple accounts — when fills, sizing, or exits diverge across prop firms, Pro surfaces the procedural leak.'
  }
];

// ────────────────────────────────────────────────────────────────────────────
// FeatureRow — shared list item for both tiers
// ────────────────────────────────────────────────────────────────────────────

const FeatureRow = ({ icon: Icon, label, detail, locked, darkMode }) => (
  <li className={`flex gap-3 p-3 rounded-lg border transition-colors ${
    locked
      ? (darkMode
          ? 'bg-white/[0.02] border-white/5 hover:bg-white/[0.04] hover:border-purple-400/30'
          : 'bg-gray-50 border-gray-200 hover:bg-white hover:border-purple-300/50')
      : (darkMode
          ? 'bg-green-500/[0.04] border-green-500/20'
          : 'bg-green-50/70 border-green-200')
  }`}>
    <div className="shrink-0">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
        locked
          ? (darkMode ? 'bg-white/5 text-purple-300' : 'bg-purple-100 text-purple-600')
          : (darkMode ? 'bg-green-500/10 text-green-400' : 'bg-green-100 text-green-600')
      }`}>
        <Icon size={16} />
      </div>
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5 mb-0.5">
        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {label}
        </p>
        {locked ? (
          <Lock size={11} className={darkMode ? 'text-purple-400' : 'text-purple-500'} />
        ) : (
          <Check size={12} className={darkMode ? 'text-green-400' : 'text-green-600'} />
        )}
      </div>
      <p className={`text-xs leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {detail}
      </p>
    </div>
  </li>
);

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

const CoachAboutModal = ({ userTier = 'free', onClose, onUpgrade, darkMode = true }) => {
  const isPro = userTier === 'pro';

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portal to document.body so we escape any ancestor that establishes a new
  // containing block for `position: fixed` children. In particular the
  // LiveCoachFeed panel uses `backdrop-blur-sm` (→ CSS `backdrop-filter`),
  // which the spec treats as a containing block for fixed descendants. Without
  // the portal, `fixed inset-0` anchors to the Coach panel — not the viewport —
  // and `items-center` ends up centering the modal in the middle of a tall
  // scrolling container, pushing it off-screen.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${
          darkMode
            ? 'bg-gray-950/95 backdrop-blur-2xl border border-white/10'
            : 'bg-white border border-gray-200'
        }`}
      >
        {/* ── Header ── */}
        <div className={`px-6 py-4 border-b flex items-center justify-between shrink-0 ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              darkMode
                ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-400/30'
                : 'bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-300'
            }`}>
              <Sparkles size={18} className={darkMode ? 'text-purple-300' : 'text-purple-600'} />
            </div>
            <div>
              <h2 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                AI Coach
              </h2>
              <p className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                You are on the <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{isPro ? 'Pro' : 'Free'}</span> tier
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-md transition-colors ${
              darkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="px-6 py-5 overflow-y-auto space-y-6">

          {/* What it does */}
          <section>
            <h3 className={`text-xs uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              What it does
            </h3>
            <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              The Coach reads your trades, session groups, news events, and ATM templates — then
              surfaces plain-English observations across three temporal scopes:{' '}
              <span className={darkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'}>TODAY</span>,{' '}
              <span className={darkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'}>GROUPS</span>, and{' '}
              <span className={darkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'}>30-DAY TRENDS</span>.
              Historical days lock their insights to memory; today’s day is live and updates as you trade.
            </p>
            <p className={`text-xs mt-2 italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              100% on-device. No external AI calls, no data leaves your machine.
            </p>
          </section>

          {/* Free tier features — always shown */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Included in Free
              </h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                darkMode ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200'
              }`}>
                Active
              </span>
            </div>
            <ul className="space-y-1.5">
              {FREE_FEATURES.map((f) => (
                <FeatureRow key={f.label} {...f} locked={false} darkMode={darkMode} />
              ))}
            </ul>
          </section>

          {/* Pro tier features */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {isPro ? 'Your Pro Features' : 'Unlocked with Pro'}
              </h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                isPro
                  ? (darkMode ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200')
                  : (darkMode ? 'bg-purple-500/15 text-purple-300 border border-purple-400/30' : 'bg-purple-100 text-purple-700 border border-purple-200')
              }`}>
                {isPro ? 'Active' : 'Locked'}
              </span>
            </div>
            <ul className="space-y-1.5">
              {PRO_FEATURES.map((f) => (
                <FeatureRow key={f.label} {...f} locked={!isPro} darkMode={darkMode} />
              ))}
            </ul>
          </section>

          {/* Data/privacy footnote */}
          <section className={`p-3 rounded-lg border ${
            darkMode ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-[11px] leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>Private by design.</span>{' '}
              Every Coach observation is computed locally from your journal. Nothing is sent to a
              server, no LLM inference runs, no telemetry is collected.
            </p>
          </section>

        </div>

        {/* ── Footer ── */}
        <div className={`px-6 py-4 border-t flex items-center justify-between shrink-0 ${
          darkMode ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'
        }`}>
          <p className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {isPro
              ? 'Thanks for supporting Pro — you unlock deeper analyzers.'
              : 'Pro unlocks deeper analyzers and historical citations.'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? 'bg-white/5 hover:bg-white/10 text-gray-200 border border-white/10'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
              }`}
            >
              {isPro ? 'Close' : 'Got it'}
            </button>
            {!isPro && (
              <button
                onClick={() => onUpgrade?.()}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-b from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white shadow-sm shadow-purple-500/30 ring-1 ring-purple-400/30 transition-all flex items-center gap-1.5"
              >
                <Sparkles size={13} />
                Upgrade to Pro
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CoachAboutModal;
